import React, { useEffect, useMemo, useState } from 'react';

import { changeOrderLeverage, getOrderLeverage } from 'actions/orderbook';
import { useOrderMargin } from 'components/basketOrders/OrdersPlacementScreen/useOrderMargin';
import { calculateMaxLeverage } from 'helpers/formulas';
import { append, pipe, sort, uniq } from 'helpers/ramda';
import { isNan } from 'helpers/utils';
import {
  allOpenPositionsAndOrdersByProduct,
  productLeverageSelector,
} from 'selectors/tradeSelectors';
import { useAppDispatch } from 'storeHooks';
import { Product } from 'types/IProducts';

import { useDefaultLeverage } from './useDefaultLeverage';

interface LeverageProps {
  product: Product;
}

const getSliderValues = (productLeverage: number, leverageSliderValues: number[]) => {
  const leverages = pipe(
    append(parseInt(String(productLeverage), 10)),
    sort((a, b) => Number(a) - Number(b)),
    uniq
  )(leverageSliderValues);
  return leverages;
};

const useLeverage = ({ product }: LeverageProps) => {
  const {
    id: productId,
    ui_config: uiConfig,
    initial_margin: initialMargin,
  } = product || {};
  const maxLeverage = initialMargin && calculateMaxLeverage(Number(initialMargin));

  const dispatch = useAppDispatch();
  const productLeverage = productLeverageSelector(product);
  const hasActiveOrderOrPosition = allOpenPositionsAndOrdersByProduct(product).length > 0;

  const [leverage, setLeverage] = useState<number | null>(() => productLeverage);
  const [sliderValues, setSliderValues] = useState(() =>
    getSliderValues(productLeverage, uiConfig?.leverage_slider_values as number[])
  );

  const { fetchAllLeverage } = useOrderMargin();
  const {
    defaultLeverage,
    handleChangeAccountLeverage,
    markAsDefault,
    changeMarkAsDefault,
  } = useDefaultLeverage(leverage);

  const isSubmitEnabled = useMemo(() => {
    let enabled = false;
    if (leverage === null || leverage <= 0) {
      return false;
    }
    if (leverage !== productLeverage) {
      enabled = true;
    }
    if (!defaultLeverage && markAsDefault) {
      enabled = true;
    }
    if (!!defaultLeverage && (leverage === Number(defaultLeverage)) !== markAsDefault) {
      enabled = true;
    }
    return enabled;
  }, [leverage, productLeverage, defaultLeverage, markAsDefault]);

  const onLeverageChange = (value: number) => {
    setLeverage(Math.floor(value));
    changeMarkAsDefault(!!defaultLeverage && value === Number(defaultLeverage));
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (!val) {
      setLeverage(null);
      return;
    }

    const changedLeverage = Number(val);

    if (isNan(changedLeverage) || changedLeverage <= 0) {
      return;
    }

    if (changedLeverage > Number(maxLeverage)) {
      return;
    }

    setLeverage(Math.floor(changedLeverage));

    const newSliderValues = getSliderValues(
      Number(changedLeverage),
      uiConfig?.leverage_slider_values as number[]
    );
    setSliderValues(
      changedLeverage < Number(maxLeverage) ? newSliderValues : sliderValues
    );
  };

  const handleLeverageDecrement = () => {
    if (!leverage) {
      setLeverage(1);
      return;
    }
    const currentIndex = sliderValues.findIndex(elem => elem === leverage);
    if (currentIndex === 0) {
      return;
    }
    setLeverage(sliderValues[currentIndex - 1]);
  };

  const handleLeverageIncrement = () => {
    if (!leverage) {
      setLeverage(1);
      return;
    }
    const currentIndex = sliderValues.findIndex(elem => elem === leverage);
    if (currentIndex === sliderValues.length - 1) {
      return;
    }
    setLeverage(sliderValues[currentIndex + 1]);
  };

  const handleLeverageSubmit = async (isBasketLeverageChange = false) => {
    if (!productId) return;
    if (markAsDefault) {
      try {
        // change account leverage
        await handleChangeAccountLeverage();

        if (isBasketLeverageChange) {
          fetchAllLeverage();
          return;
        }
        dispatch(getOrderLeverage(productId));
      } catch (err) {
        console.error(err);
      }
    } else {
      // change order leverage
      dispatch(changeOrderLeverage(productId, leverage || 1));
    }
  };

  const toggleSetDefaultLeverage = () => {
    changeMarkAsDefault(!markAsDefault);
  };

  useEffect(() => {
    setSliderValues(() =>
      getSliderValues(productLeverage, uiConfig?.leverage_slider_values as number[])
    );
  }, [product, productLeverage, uiConfig?.leverage_slider_values]);

  useEffect(() => {
    setLeverage(productLeverage);
    changeMarkAsDefault(!!defaultLeverage && productLeverage === Number(defaultLeverage));
  }, [productLeverage, defaultLeverage, changeMarkAsDefault]);

  return {
    leverage,
    maxLeverage,
    sliderValues,
    markAsDefault,
    isSubmitEnabled,
    defaultLeverage,
    hasActiveOrderOrPosition,
    onInputChange,
    onLeverageChange,
    handleLeverageSubmit,
    handleLeverageDecrement,
    handleLeverageIncrement,
    toggleSetDefaultLeverage,
  };
};

export default useLeverage;
